import React from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const PhotoCreditsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="text-3xl font-semibold tracking-tight mb-4">
            Photo Credits
          </h1>

          <p className="text-sm text-gray-600 mb-8">
            SafeSheAtlas uses photos from multiple open and community-driven
            image providers. We thank all photographers and platforms whose
            work helps us visually represent each destination.
          </p>

          {/* Wikimedia Commons Section */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-3">Wikimedia Commons</h2>
            <p className="text-sm leading-relaxed text-gray-700">
              Many destination skyline and cityscape photos are sourced from{" "}
              <a
                href="https://commons.wikimedia.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Wikimedia Commons
              </a>
              , contributed by volunteer photographers worldwide. These
              images are used under their respective Creative Commons or
              public-domain licenses. Each image below links back to its
              original file page.
            </p>
          </section>

          {/* Unsplash Section */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-3">Unsplash</h2>
            <p className="text-sm leading-relaxed text-gray-700">
              Some destination and background images are provided by{" "}
              <a
                href="https://unsplash.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Unsplash
              </a>
              . We thank the photographers whose work is used in accordance
              with the Unsplash License.
            </p>
          </section>

          {/* Destination Image Credits */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">
              Destination Image Credits
            </h2>

            <ul className="space-y-4 text-sm leading-relaxed text-gray-700">

              <li>
                <strong>Stockholm, Sweden</strong> — Photo by{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/User:Tony_Webb"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Tony Webster
                </a>
                , via Wikimedia Commons. Licensed under{" "}
                <a
                  href="https://creativecommons.org/licenses/by/2.0/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  CC BY 2.0
                </a>
                .{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Stockholm_Skyline_(15189547020).jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

              <li>
                <strong>Ho Chi Minh City, Vietnam</strong> — Public Domain
                image via Wikimedia Commons.{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Ho_Chi_Minh_City_Skyline.jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

              <li>
                <strong>Oslo, Norway</strong> — Licensed under{" "}
                <a
                  href="https://creativecommons.org/licenses/by/2.0/"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CC BY 2.0
                </a>
                .{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Oslo_skyline_on_sunset.jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

              <li>
                <strong>Jeddah, Saudi Arabia</strong> — Public Domain via
                Wikimedia Commons.{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Jeddah_Seafront.jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

              <li>
                <strong>Jakarta, Indonesia</strong> — Licensed under{" "}
                <a
                  href="https://creativecommons.org/licenses/by/2.0/"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CC BY 2.0
                </a>
                .{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Jakarta_Skyline_Jakarta_2015.jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

              <li>
                <strong>Doha, Qatar</strong> — Licensed under{" "}
                <a
                  href="https://creativecommons.org/licenses/by/2.0/"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CC BY 2.0
                </a>
                .{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Doha_skyline_in_the_morning.jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

              <li>
                <strong>Miami, United States</strong> — Public Domain image
                via Wikimedia Commons.{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Miami_Skyline_09.jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

              <li>
                <strong>Florence, Italy</strong> — Licensed under{" "}
                <a
                  href="https://creativecommons.org/licenses/by/2.0/"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CC BY 2.0
                </a>
                .{" "}
                <a
                  href="https://commons.wikimedia.org/wiki/File:Florence_Skyline_(15243251005).jpg"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>

            </ul>
          </section>

          {/* General Attribution */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-2">General Attribution</h2>
            <p className="text-sm leading-relaxed text-gray-700">
              All photos are used for illustrative purposes only and do not
              imply endorsement of SafeSheAtlas by the photographers or
              platforms. If you are an image creator and would like your photo
              to be updated or removed, please contact us at{" "}
              <a
                href="mailto:hello@safesheatlas.com"
                className="underline"
              >
                hello@safesheatlas.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PhotoCreditsPage;
